import { useMemo } from "react";
import { Avatar, type AvatarProps } from "@mui/material";

type Props = {
  name?: string | null;
  email?: string | null;
  size?: number;
} & Omit<AvatarProps, "src" | "alt">;

function pickInitials(name?: string | null, email?: string | null): string {
  const source = (name || "").trim() || (email || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  const head = parts[0] || "";
  if (head.includes("@")) return head[0]!.toUpperCase();
  return head.slice(0, 2).toUpperCase();
}

function colorFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}deg 55% 45%)`;
}

export default function InitialsAvatar({ name, email, size = 32, sx, ...rest }: Props): JSX.Element {
  const initials = useMemo(() => pickInitials(name, email), [name, email]);
  const bg = useMemo(() => colorFor(`${name ?? ""}|${email ?? ""}`), [name, email]);
  return (
    <Avatar
      {...rest}
      sx={{
        width: size,
        height: size,
        bgcolor: bg,
        fontSize: size * 0.42,
        fontWeight: 600,
        color: "#fff",
        ...(sx || {}),
      }}
    >
      {initials}
    </Avatar>
  );
}
